import type { Academy } from '@court-sync/shared';
import {
  academyPublicBaseUrl,
  academyPublicPath,
  type AcademyPublicUrlOptions,
} from '@court-sync/shared';
import { getAcademyHostContext, type AcademyHostMode } from './academyHost';

export function getAcademyHostMode(hostname?: string): AcademyHostMode {
  return getAcademyHostContext(hostname).mode;
}

type AcademyPathFields = Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>;

/** App origin for path-mode URLs when the user is on a branded subdomain (Firebase continueUrl fallback). */
function resolveWebAppOriginForPathFallback(): string {
  if (typeof window === 'undefined') return 'https://app.courtsync.io';
  const { hostname, port } = window.location;
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    const portSuffix = port ? `:${port}` : '';
    return `http://localhost${portSuffix}`;
  }
  if (hostname.endsWith('.dev.courtsync.io') || hostname === 'dev.courtsync.io') {
    return 'https://app.dev.courtsync.io';
  }
  return 'https://app.courtsync.io';
}

/** Relative academy-scoped path for the current host mode. */
export function buildAcademyPath(
  academy: AcademyPathFields,
  subpath: string,
  hostMode: AcademyHostMode = getAcademyHostMode()
): string {
  return academyPublicPath(academy, subpath, hostMode);
}

/** Absolute public URL for an academy path (emails, share links). */
export function buildAcademyAbsoluteUrl(
  academy: AcademyPathFields,
  subpath = '',
  options?: AcademyPublicUrlOptions,
  hostMode: AcademyHostMode = 'path'
): string {
  return academyPublicBaseUrl(academy, subpath, options, hostMode);
}

/** Login path for the current host (subdomain or path mode). */
export function buildAcademyLoginPath(
  academy: AcademyPathFields,
  returnPath?: string,
  hostMode: AcademyHostMode = getAcademyHostMode()
): string {
  const loginPath = buildAcademyPath(academy, '/login', hostMode);
  if (!returnPath) return loginPath;
  const sep = loginPath.includes('?') ? '&' : '?';
  return `${loginPath}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

function appendReturnUrlToAbsolute(url: string, returnPath?: string): string {
  if (!returnPath) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/** Forgot-password path for the current host. */
export function buildAcademyForgotPasswordPath(
  academy: AcademyPathFields,
  returnPath?: string,
  hostMode: AcademyHostMode = getAcademyHostMode()
): string {
  const path = buildAcademyPath(academy, '/forgot-password', hostMode);
  if (!returnPath) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/** Register path for the current host. */
export function buildAcademyRegisterPath(
  academy: AcademyPathFields,
  returnPath?: string,
  hostMode: AcademyHostMode = getAcademyHostMode()
): string {
  const path = buildAcademyPath(academy, '/register', hostMode);
  if (!returnPath) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}returnUrl=${encodeURIComponent(returnPath)}`;
}

/**
 * Firebase password-reset continue URLs. Subdomain hosts may be unauthorized in Firebase;
 * fallback uses path-mode app origin (always authorized).
 */
export function buildPasswordResetContinueUrls(
  academy: AcademyPathFields,
  returnPath?: string,
  _hostMode: AcademyHostMode = getAcademyHostMode()
): { primary: string; fallback: string } {
  const pathReset = academyPublicPath(
    { ...academy, subdomainEnabled: false },
    '/reset-password',
    'path'
  );
  const pathModeUrl = appendReturnUrlToAbsolute(
    `${resolveWebAppOriginForPathFallback()}${pathReset}`,
    returnPath
  );

  // Always use path-mode app origin for Firebase continue URLs (authorized domain).
  // Subdomain hosts are often not in Firebase authorized domains; fallback retry on send
  // failure does not help when Mailgun accepts the request with a bad continue URL.
  return { primary: pathModeUrl, fallback: pathModeUrl };
}
